import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Images,
  Folder,
  MessageSquare,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Home,
  Settings,
  Layers,
  Users,
  Key,
  Menu,
  X,
  ChevronDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";

interface SidebarProps {
  className?: string;
}

interface SidebarItem {
  title: string;
  icon: React.ElementType;
  href: string;
  isHeader?: boolean;
  submenu?: Array<{ title: string; href: string }>;
}

export function Sidebar({ className }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const { logout } = useAuth();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);

  // Ekran boyutu değişikliklerini izle
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setCollapsed(true);
      } else {
        setCollapsed(false); // Masaüstü boyutuna dönüldüğünde kapatmayı kaldır
      }
    };

    // İlk yüklendiğinde kontrol et
    handleResize();

    // Ekran boyutu değiştiğinde kontrol et
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Mobil görünümde sayfa değiştiğinde menüyü kapat
  useEffect(() => {
    if (isMobile) {
      setMobileOpen(false);
    }
  }, [location.pathname, isMobile]);

  const sidebarItems: SidebarItem[] = [
    { title: "Dashboard", icon: LayoutDashboard, href: "/dashboard" },
    { title: "İçerik", icon: FileText, href: "#", isHeader: true },
    {
      title: "Anasayfa",
      icon: Home,
      href: "#",
      submenu: [
        { title: "Slayt", href: "/content/home/slides" },
        { title: "Hizmetler", href: "/content/home/services" },
        { title: "Projeler", href: "/content/home/projects" }
      ]
    },
    {
      title: "Portföy",
      icon: Layers,
      href: "#",
      submenu: [
        { title: "Yazı", href: "/content/portfolio/content" }
      ]
    },
    {
      title: "Hizmetler",
      icon: Settings,
      href: "#",
      submenu: [
        { title: "Yazı", href: "/content/services/content" },
        { title: "Kategori", href: "/content/services/categories" }
      ]
    },
    {
      title: "Hakkımızda",
      icon: Users,
      href: "#",
      submenu: [
        { title: "Yazı", href: "/content/about/content" },
        { title: "Uzmanlık Alanlarımız", href: "/content/about/expertise" }
      ]
    },
    { title: "Portföy", icon: Folder, href: "#", isHeader: true },
    { title: "Kategori Yönetimi", icon: Folder, href: "/portfolio/categories" },
    { title: "Proje Yönetimi", icon: Images, href: "/portfolio/projects" },
    { title: "Mesajlar", icon: MessageSquare, href: "/messages" },
    { title: "Ayarlar", icon: Settings, href: "#", isHeader: true },
    { title: "Şifre Değiştir", icon: Key, href: "/change-password" }
  ];

  const toggleSubmenu = (title: string) => {
    if (openSubmenu === title) {
      setOpenSubmenu(null);
    } else {
      setOpenSubmenu(title);
    }
  };

  const isActiveRoute = (href: string) => {
    if (href === "#") return false;
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  // Mobil menü açma/kapama
  const toggleMobileMenu = () => {
    setMobileOpen(!mobileOpen);
  };

  // Mobil görünümde alt menü öğeleri direkt butonlar olarak gösterilir
  const renderMobileMenuItem = (item: SidebarItem, idx: number) => {
    if (item.submenu) {
      return (
        <div key={`mobile-item-${idx}`} className="flex flex-col">
          <div className={cn(
            "flex items-center gap-2 rounded-md p-2 text-sidebar-foreground",
            item.submenu.some(sub => isActiveRoute(sub.href)) && "bg-sidebar-accent/30 font-medium"
          )}>
            <item.icon className="h-4 w-4" />
            <span className="font-medium">{item.title}</span>
            <ChevronDown
              className={cn(
                "ml-auto h-4 w-4 transition-transform",
                openSubmenu === item.title && "rotate-180"
              )}
              onClick={() => toggleSubmenu(item.title)}
            />
          </div>

          {openSubmenu === item.title && (
            <div className="pl-6 mt-1 space-y-1 border-l-2 border-sidebar-accent ml-2">
              {item.submenu.map((subItem, subIdx) => (
                <Link
                  key={`mobile-subitem-${subIdx}`}
                  to={subItem.href}
                  className={cn(
                    "flex w-full px-2 py-1.5 text-sm rounded-md text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors",
                    isActiveRoute(subItem.href) && "bg-sidebar-accent/50 font-medium"
                  )}
                  onClick={() => setMobileOpen(false)}
                >
                  {subItem.title}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    } else {
      return (
        <Link
          key={`mobile-item-${idx}`}
          to={item.href}
          className={cn(
            "flex items-center gap-2 rounded-md p-2 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors",
            isActiveRoute(item.href) && "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
          )}
          onClick={() => setMobileOpen(false)}
        >
          <item.icon className="h-4 w-4" />
          <span>{item.title}</span>
        </Link>
      );
    }
  };

  // Masaüstü görünümde alt menüler dropdown şeklinde
  const renderDesktopMenuItem = (item: SidebarItem, idx: number) => {
    if (item.submenu) {
      return (
        <div key={`desktop-submenu-${idx}`} className="relative">
          <button
            className={cn(
              "w-full flex items-center gap-2 rounded-md p-2 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors",
              item.submenu.some(sub => isActiveRoute(sub.href)) && "bg-sidebar-accent font-medium",
              collapsed && "justify-center"
            )}
            onClick={() => toggleSubmenu(item.title)}
          >
            <item.icon className={cn("h-4 w-4", collapsed && "h-5 w-5")} />
            {!collapsed && (
              <>
                <span>{item.title}</span>
                <ChevronRight
                  className={cn(
                    "ml-auto h-4 w-4 transition-transform",
                    openSubmenu === item.title && "rotate-90"
                  )}
                />
              </>
            )}
          </button>

          {openSubmenu === item.title && (
            <div
              className={cn(
                "z-50 bg-sidebar border border-sidebar-border shadow-md rounded-md p-2",
                collapsed
                  ? "absolute left-full top-0 min-w-40 -mt-2 ml-1"
                  : "ml-6 my-1 space-y-1"
              )}
            >
              {item.submenu.map((subItem, subIdx) => (
                <Link
                  key={`desktop-subitem-${subIdx}`}
                  to={subItem.href}
                  className={cn(
                    "flex px-2 py-1.5 text-sm rounded-md text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors",
                    isActiveRoute(subItem.href) && "bg-sidebar-accent/50 font-medium"
                  )}
                >
                  {subItem.title}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    } else {
      return (
        <Link
          key={`desktop-item-${idx}`}
          to={item.href}
          className={cn(
            "flex items-center gap-2 rounded-md p-2 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors",
            isActiveRoute(item.href) && "bg-sidebar-accent text-sidebar-accent-foreground font-medium",
            collapsed && "justify-center"
          )}
        >
          <item.icon className={cn("h-4 w-4", collapsed && "h-5 w-5")} />
          {!collapsed && <span>{item.title}</span>}
        </Link>
      );
    }
  };

  return (
    <>
      {/* Mobil menü toggle düğmesi - Sadece sidebar kapalıyken görünür ve her zaman Menu ikonu gösterir */}
      {!mobileOpen && ( // Sadece mobil sidebar kapalıyken bu tuşu göster
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden fixed top-4 left-4 z-50 bg-background/80 backdrop-blur-sm"
          onClick={toggleMobileMenu}
        >
          <Menu size={20} /> {/* Her zaman menü ikonu göster */}
        </Button>
      )}

      {/* Mobil Menü - Kenar çubuğu */}
      {isMobile && (
        <div
          className={cn(
            "flex flex-col h-screen bg-sidebar border-r border-sidebar-border transition-all duration-300 w-64",
            "fixed z-40",
            mobileOpen ? "left-0" : "-left-full",
            className
          )}
        >
          <div className="flex items-center justify-between h-14 px-4 border-b border-sidebar-border">
            <h2 className="text-lg font-medium text-sidebar-foreground">Admin Panel</h2>
            {/* Sidebarın içindeki kapatma tuşu - sağ üst köşede */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMobileMenu}
            >
              <X size={18} />
            </Button>
          </div>

          <ScrollArea className="flex-1">
            <div className="px-2 py-2">
              {sidebarItems.map((item, idx) => {
                if (item.isHeader) {
                  return (
                    <div
                      key={`mobile-header-${idx}`}
                      className="text-xs font-medium text-sidebar-foreground/70 py-2 px-2 mt-4 uppercase"
                    >
                      {item.title}
                    </div>
                  );
                } else {
                  return renderMobileMenuItem(item, idx);
                }
              })}
            </div>
          </ScrollArea>

          <div className="border-t border-sidebar-border p-2 mt-auto">
            <div className="flex items-center justify-between mb-2">
              <ThemeToggle className="h-8 w-14" />
              <Button
                variant="ghost"
                className={cn(
                  "flex items-center gap-2 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors",
                  collapsed && "w-full justify-center"
                )}
                onClick={logout}
              >
                <LogOut className="h-4 w-4" />
                {!collapsed && <span>Çıkış Yap</span>}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Masaüstü Menü - Kenar çubuğu */}
      {!isMobile && (
        <div
          className={cn(
            "flex flex-col h-screen bg-sidebar border-r border-sidebar-border transition-all duration-300",
            collapsed ? "w-16" : "w-60",
            "relative",
            className
          )}
        >
          <div className="flex items-center justify-between h-14 px-4 border-b border-sidebar-border">
            {!collapsed && (
              <h2 className="text-lg font-medium text-sidebar-foreground">Admin Panel</h2>
            )}
            <Button
              variant="ghost"
              size="icon"
              className={cn("ml-auto", collapsed && "mx-auto")}
              onClick={() => setCollapsed(!collapsed)}
            >
              {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </Button>
          </div>

          <ScrollArea className="flex-1">
            <div className="px-2 py-2">
              {sidebarItems.map((item, idx) => {
                if (item.isHeader) {
                  return (
                    <div
                      key={`desktop-header-${idx}`}
                      className={cn(
                        "text-xs font-medium text-sidebar-foreground/70 py-2 px-2 mt-4",
                        collapsed ? "sr-only" : "uppercase"
                      )}
                    >
                      {item.title}
                    </div>
                  );
                } else {
                  return renderDesktopMenuItem(item, idx);
                }
              })}
            </div>
          </ScrollArea>

          <div className="border-t border-sidebar-border p-2 mt-auto">
            <div className="flex items-center justify-between mb-2">
              <ThemeToggle className="h-8 w-14" />
              <Button
                variant="ghost"
                className={cn(
                  "flex items-center gap-2 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors",
                  collapsed && "w-full justify-center"
                )}
                onClick={logout}
              >
                <LogOut className="h-4 w-4" />
                {!collapsed && <span>Çıkış Yap</span>}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mobil görünüm için arka plan overlay */}
      {mobileOpen && isMobile && (
        <div
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-30"
          onClick={toggleMobileMenu}
        />
      )}
    </>
  );
}